import React, { useEffect, useState, useCallback } from "react";
import { UsersThree, Trophy, Medal, Barbell, Crown } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOWS = [
  { id: "7d", label: "7 DAYS" },
  { id: "30d", label: "30 DAYS" },
  { id: "all", label: "ALL TIME" },
];

const rankBadge = (rank) => {
  if (rank === 1) return "bg-brand-accent text-surface-base";
  if (rank === 2) return "bg-white/80 text-surface-base";
  if (rank === 3) return "bg-amber-700/80 text-white";
  return "bg-white/5 text-text-secondary";
};

const avatar = (m) => {
  if (m.picture) {
    const src = m.picture.startsWith("/api/") ? `${BACKEND_URL}${m.picture}` : m.picture;
    return <img src={src} alt="" className="w-9 h-9 rounded-full object-cover" />;
  }
  return (
    <div className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center text-xs font-bold">
      {(m.name || "?")[0].toUpperCase()}
    </div>
  );
};

export default function Teams() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [board, setBoard] = useState(null);
  const [win, setWin] = useState("7d");

  useEffect(() => {
    setLoading(true);
    api.get("/my/teams")
      .then(({ data }) => {
        setTeams(data);
        if (data.length && !selectedId) setSelectedId(data[0].team_id);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const loadBoard = useCallback(() => {
    if (!selectedId) return;
    api.get(`/my/teams/${selectedId}/leaderboard?window=${win}`)
      .then(({ data }) => setBoard(data))
      .catch(() => setBoard(null));
  }, [selectedId, win]);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  const selectedTeam = teams.find((t) => t.team_id === selectedId);

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="teams-page">
      <div>
        <div className="label-tiny text-brand-accent tracking-widest mb-2">// ATHLETE · TEAMS</div>
        <h1 className="font-heading text-4xl sm:text-5xl">My Teams</h1>
        <p className="text-text-secondary mt-2 max-w-2xl text-sm">
          Compete with your team. Rankings update automatically from your workouts and new PRs.
        </p>
      </div>

      {loading ? (
        <div className="text-text-secondary label-tiny" data-testid="teams-loading">Loading...</div>
      ) : teams.length === 0 ? (
        <div className="border border-white/10 p-10 text-center" data-testid="teams-empty">
          <UsersThree size={48} className="mx-auto text-brand-accent mb-3" weight="duotone" />
          <div className="font-heading text-xl mb-1">You're not on a team yet</div>
          <div className="text-text-secondary text-sm">
            Ask your coach to add you to a team — you'll see the leaderboard here.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Team list */}
          <div className="space-y-2" data-testid="teams-list">
            {teams.map((t) => (
              <button
                key={t.team_id}
                onClick={() => setSelectedId(t.team_id)}
                data-testid={`team-select-${t.team_id}`}
                className={`w-full text-left border p-4 transition ${
                  selectedId === t.team_id
                    ? "border-brand-accent bg-brand-accent/5"
                    : "border-white/10 hover:border-white/30"
                }`}
              >
                <div className="label-tiny text-brand-accent/80 text-[10px] tracking-widest mb-1">// TEAM</div>
                <div className="font-heading text-lg truncate">{t.name}</div>
                <div className="text-xs text-text-secondary mt-1">
                  <UsersThree size={11} className="inline mr-1" /> {t.member_count} members
                </div>
                {t.coach?.name && (
                  <div className="text-xs text-text-secondary mt-0.5 truncate">Coach: {t.coach.name}</div>
                )}
              </button>
            ))}
          </div>

          {/* Leaderboard */}
          <div className="border border-white/10 bg-white/[0.02]" data-testid="teams-leaderboard">
            {selectedTeam && (
              <>
                <div className="flex items-center justify-between gap-3 flex-wrap p-5 border-b border-white/10">
                  <div>
                    <div className="flex items-center gap-2">
                      <Trophy size={18} className="text-brand-accent" weight="duotone" />
                      <div className="font-heading text-xl truncate">{selectedTeam.name}</div>
                    </div>
                    {selectedTeam.description && (
                      <div className="text-xs text-text-secondary mt-1 max-w-md">{selectedTeam.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 border border-white/10 p-1" data-testid="teams-window-toggle">
                    {WINDOWS.map((w) => (
                      <button
                        key={w.id}
                        onClick={() => setWin(w.id)}
                        data-testid={`teams-window-${w.id}`}
                        className={`px-3 py-1.5 text-xs label-tiny tracking-wider transition ${
                          win === w.id ? "bg-brand-accent text-surface-base" : "text-text-secondary hover:text-white"
                        }`}
                      >
                        {w.label}
                      </button>
                    ))}
                  </div>
                </div>

                {(!board || board.entries.length === 0) ? (
                  <div className="p-10 text-center text-text-secondary" data-testid="teams-leaderboard-empty">
                    <Medal size={40} className="mx-auto text-brand-accent mb-2" weight="duotone" />
                    <div className="text-sm">No activity in this window yet. Log a workout to jump on the board.</div>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {board.entries.map((e) => {
                      const isMe = e.user_id === user?.user_id;
                      return (
                        <div
                          key={e.user_id}
                          className={`flex items-center gap-4 p-4 ${isMe ? "bg-brand-accent/5" : ""}`}
                          data-testid={`teams-lb-row-${e.user_id}`}
                        >
                          <div className={`w-9 h-9 flex items-center justify-center text-sm font-bold ${rankBadge(e.rank)}`}>
                            {e.rank === 1 ? <Crown size={16} weight="fill" /> : `#${e.rank}`}
                          </div>
                          {avatar(e)}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {e.name} {isMe && <span className="text-brand-accent text-[10px] ml-1">YOU</span>}
                            </div>
                            <div className="text-xs text-text-secondary flex gap-3">
                              <span><Barbell size={11} className="inline mr-0.5" /> {e.workouts} workouts</span>
                              <span><Trophy size={11} className="inline mr-0.5" /> {e.prs} PRs</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-heading text-brand-accent tabular-nums">{e.points}</div>
                            <div className="text-[10px] label-tiny text-text-secondary">PTS</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="p-3 border-t border-white/5 text-[10px] label-tiny text-text-secondary/70 tracking-wider">
                  POINTS = WORKOUTS × 10 + NEW PRs × 25
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
